import { uuidv7 } from "uuidv7"

import type { SearchListPageResponseDatagramBuf } from "~/classes/generated/response"
import type { SearchListPageResponseDatagram } from "~/models/polyschema/SearchListPageResponseDatagram"
import type { SearchListRequestDatagram } from "~/models/polyschema/SearchListRequestDatagram"

import { PropertyId, PropertySchema, PropertyStatus } from "~/classes/entity"
import { DatagramBuf, DatagramErrorTypeBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SearchListRequestDatagramBuf } from "~/classes/generated/request"
import { StaticSearch } from "~/classes/search/StaticSearch"
import { StaticUser } from "~/classes/users/StaticUser"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId, parseVector } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf, parseEntityPropertySelection } from "~/utils/flatbuffers/entity"
import { parseFile } from "~/utils/flatbuffers/file"
import { addSearchIdToBuf, parseSearchId } from "~/utils/flatbuffers/id"
import { addSearchToBuf, parseSearch } from "~/utils/flatbuffers/search"
import { maxElement, maybe } from "~/utils/helpers"

// We log the start time of every search list request
const startTimes: Record<string, number> = {}
const console = useLogger("search/list", theme.colors.pink.hex)

export function sendSearchListRequests(requests: SearchListRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  for (const request of requests) {
    startTimes[request.requestId] = Date.now()
  }

  const builder = new DatagramBufferBuilder(64)
  const requestOffsets: number[] = []

  for (const request of requests) {
    const search = addSearchToBuf(builder, request.search)
    let preHash
    if (request.preHash)
      preHash = addSearchIdToBuf(builder, request.preHash)
    const fileSelection = addEntityPropertySelectionToBuf(builder, request.fileSelection)
    const pathSelection = addEntityPropertySelectionToBuf(builder, request.pathSelection)
    const requestId = addRequestIdToBuf(builder, request.requestId)
    SearchListRequestDatagramBuf.startSearchListRequestDatagramBuf(builder)
    SearchListRequestDatagramBuf.addRequestId(builder, requestId)
    SearchListRequestDatagramBuf.addSearch(builder, search)
    if (preHash)
      SearchListRequestDatagramBuf.addPreHash(builder, preHash)

    SearchListRequestDatagramBuf.addFileSelection(builder, fileSelection)
    SearchListRequestDatagramBuf.addPathSelection(builder, pathSelection)
    SearchListRequestDatagramBuf.addPageSize(builder, request.pageSize || 50) // FIXME: update this
    SearchListRequestDatagramBuf.addOffline(builder, request.offline)
    SearchListRequestDatagramBuf.addLimit(builder, request.limit)
    SearchListRequestDatagramBuf.addOffset(builder, request.offset)

    const listRequest = SearchListRequestDatagramBuf.endSearchListRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addSearchListRequest(builder, listRequest)
    requestOffsets.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const requestBuffers = DatagramBuf.createRequestVector(builder, requestOffsets)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, requestBuffers)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

export function parseSearchListRequestDatagramBuf(datagram: SearchListRequestDatagramBuf): SearchListRequestDatagram {
  // we must be sure to check the error first.
  const search = parseSearch(datagram.search()!)
  const preHash = maybe(parseSearchId)(datagram.preHash())
  const fileSelection = parseEntityPropertySelection(datagram.fileSelection()!)
  const pathSelection = parseEntityPropertySelection(datagram.pathSelection()!)

  return {
    requestId: parseRequestId(datagram.requestId()!),
    search,
    preHash,
    fileSelection,
    pathSelection,
    pageSize: datagram.pageSize(),
    offline: datagram.offline(),
    limit: datagram.limit(),
    offset: datagram.offset(),
  }
}

export function parseSearchListPageResponseDatagramBuf(
  datagram: SearchListPageResponseDatagramBuf,
): SearchListPageResponseDatagram {
  // we must be sure to check the error first.
  const searchId = parseSearchId(datagram.searchId()!)
  const requestId = parseRequestId(datagram.requestId()!)
  const error = maybe(parseDatagramError)(datagram.error())
  const search = maybe(parseSearch)(datagram.search())
  const files = parseVector(i => datagram.files(i)!, datagram.filesLength()).map(parseFile)

  return {
    searchId,
    requestId,
    error,
    search,
    files,
    index: datagram.index(),
    offline: datagram.offline(),
    limit: datagram.limit(),
    pageSize: datagram.pageSize(),
    last: datagram.last(),
  }
}

export function handleSearchListResponseDatagramBuf(datagram: SearchListPageResponseDatagramBuf) {
  const response = parseSearchListPageResponseDatagramBuf(datagram)

  const startTime = startTimes[response.requestId]
  if (startTime)
    console.debug("Search list latency (ms):", Date.now() - startTime)

  onSearchListPageResponse(response)
}

function onSearchListPageResponse(
  response: SearchListPageResponseDatagram,
) {
  const { emitInterfaceEvent, emitEntityEvent } = useEvents()

  console.debug("Search list response", response)

  emitInterfaceEvent("searchListPageResponse", response, response.requestId)

  if (response.error)
    return onSearchListPageErrored(response.error, response.requestId, response.searchId, response.offline)
  if (!isDefined(response.files) || !isDefined(response.search))
    throw new Error("No files in response")

  emitEntityEvent("searchProperties", response.searchId, response.search, response.offline)

  // we need to add the file virtual search relevance property here for the UI to be able to
  // sort and whatnot for files. We only want to add the property to the files returned to
  // the 'search result' entity event, however.
  const iSearch = new StaticSearch(response.search)
  const results = iSearch.searchResults()
  if (len(results) !== len(response.files))
    console.error("Search results is malformed", response, results)

  // This gives us an array of search results per file in case there are multiple
  const resultsByFile = groupedDict(Object.values(results), "fileId")

  // send all of our file events based on the contents of this page
  const searchId = response.searchId
  for (const file of response.files) {
    emitEntityEvent("fileProperties", file.id, file, response.offline)

    const fileResults = resultsByFile[file.id]
    if (!isDefined(fileResults) || !fileResults.length) {
      console.error("No search results for file", file, results)
      continue
    }

    // get the top score to place in search result property
    // TODO: what if we have multiple of the same file in the search?
    // Or what if we have the same file appear in multiple pages?
    const result = maxElement(fileResults, r => r.score)!

    file.properties[PropertyId.FILE_VIRTUAL_SEARCH_RESULT] = {
      id: file.id,
      propertyId: PropertyId.FILE_VIRTUAL_SEARCH_RESULT,
      value: { type: PropertySchema.SearchResultValue, result },
      timestamp: uuidv7(),
      status: PropertyStatus.StatusCurrent,
      schema: PropertySchema.SearchResultValue,
      version: 0,
      author: StaticUser.generateClientOnlyUserId(),
    }

    // aggregate all matched chunks for this file in the search results
    const snippets = fileResults.flatMap(r => r.snippets ?? [])
    file.properties[PropertyId.FILE_VIRTUAL_SNIPPETS] = {
      id: file.id,
      propertyId: PropertyId.FILE_VIRTUAL_SNIPPETS,
      value: { type: PropertySchema.FileSnippetsValue, snippets },
      timestamp: uuidv7(),
      status: PropertyStatus.StatusCurrent,
      schema: PropertySchema.FileSnippetsValue,
      version: 0,
      author: StaticUser.generateClientOnlyUserId(),
    }

    emitEntityEvent("searchResult", searchId, file, response.offline)
  }
}

function onSearchListPageErrored(
  error: DatagramError,
  _requestId: string,
  searchId: string,
  offline: boolean,
) {
  const { navigateToSelfHome, currentSearch } = useNavigation()
  const { notifyDatagramError } = useAlerts()
  const { isOffline } = useCache()

  // NOTE: We only want to send these errors if this search request corresponds
  // with the user's current directory, as otherwise the user might get too many
  // notifications for non-userspace jobs.
  if (searchId === currentSearch.value?.id) {
    if (error.type === DatagramErrorTypeBuf.SearchNotFound && !offline) {
      // The file was not found, we should mention it wasn't found then redirect
      // to the home directory.
      navigateToSelfHome()
      return notifyDatagramError(error, "That search doesn't seem to exist anymore")
    }
    else if (error.type === DatagramErrorTypeBuf.SearchNotFound && offline && !isOffline())
      return console.info("Offline search not found, waiting for server response")
    else
      return notifyDatagramError(error, "There was a problem listing the search")
  }

  return console.warn("Search list errored", error)
}
